import { useState, useMemo, useCallback } from 'react';

export const useProductSelection = (initialProducts = []) => {
  const [products, setProducts] = useState(initialProducts);

  const selectionStats = useMemo(() => {
    const categoriesWithProducts = products.filter(
      (cat) => cat.products && cat.products.length > 0
    );
    const totalCategories = categoriesWithProducts.length;
    const checkedCategories = categoriesWithProducts.filter(
      (cat) => cat.checked
    ).length;
    const totalProducts = categoriesWithProducts.reduce(
      (acc, cat) => acc + (cat.products?.length || 0),
      0
    );
    const checkedProducts = categoriesWithProducts.reduce(
      (acc, cat) =>
        acc + (cat.products?.filter((prod) => prod.checked).length || 0),
      0
    );
    return {
      totalCategories,
      checkedCategories,
      totalProducts,
      checkedProducts,
      allCategoriesChecked:
        checkedCategories === totalCategories && totalCategories > 0,
      noCategoriesChecked: checkedCategories === 0,
    };
  }, [products]);

  const overallSelectionState = useMemo(() => {
    const { allCategoriesChecked, noCategoriesChecked } = selectionStats;
    if (noCategoriesChecked) return 'none';
    if (allCategoriesChecked) return 'all';
    return 'partial';
  }, [selectionStats]);

  const getCategoryState = useCallback(
    (categoryId) => {
      const category = products.find((cat) => cat.id === categoryId);
      if (!category?.products?.length) {
        return { checked: false, indeterminate: false };
      }
      const checkedProducts = category.products.filter((prod) => prod.checked);
      const allChecked = checkedProducts.length === category.products.length;
      const someChecked = checkedProducts.length > 0;
      return {
        checked: allChecked,
        indeterminate: someChecked && !allChecked,
      };
    },
    [products]
  );

  const toggleAll = useCallback(() => {
    const shouldCheckAll = overallSelectionState !== 'all';
    setProducts((currentProducts) =>
      currentProducts.map((category) => ({
        ...category,
        checked: shouldCheckAll,
        products:
          category.products?.map((product) => ({
            ...product,
            checked: shouldCheckAll,
          })) || [],
      }))
    );
  }, [overallSelectionState]);

  const toggleCategory = useCallback((categoryId) => {
    setProducts((currentProducts) =>
      currentProducts.map((category) => {
        if (category.id !== categoryId) return category;
        const shouldCheck = !category.checked;
        return {
          ...category,
          checked: shouldCheck,
          products:
            category.products?.map((product) => ({
              ...product,
              checked: shouldCheck,
            })) || [],
        };
      })
    );
  }, []);

  const toggleProduct = useCallback((categoryId, productId) => {
    setProducts((currentProducts) =>
      currentProducts.map((category) => {
        if (category.id !== categoryId) return category;
        const updatedProducts =
          category.products?.map((product) => {
            if (product.id !== productId) return product;
            return { ...product, checked: !product.checked };
          }) || [];
        const checkedProductsCount = updatedProducts.filter(
          (prod) => prod.checked
        ).length;
        const categoryChecked = checkedProductsCount > 0;
        return {
          ...category,
          checked: categoryChecked,
          products: updatedProducts,
        };
      })
    );
  }, []);

  const getSelectedProductIds = useCallback(() => {
    return products.flatMap((category) =>
      (category.products || [])
        .filter((product) => product.checked)
        .map((product) => product.id)
    );
  }, [products]);

  const updateProducts = useCallback((newProducts) => {
    setProducts(newProducts);
  }, []);

  return {
    products,
    selectionStats,
    overallSelectionState,
    getCategoryState,
    toggleAll,
    toggleCategory,
    toggleProduct,
    getSelectedProductIds,
    updateProducts,
  };
};
